import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["backdrop", "choicePanel", "guestPanel"]

  connect() {
    this.handleEscapeKey = this.handleEscapeKey.bind(this)
    document.addEventListener("keydown", this.handleEscapeKey)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscapeKey)
  }

  openModal() {
    // Step 1: Setup - make backdrop flex but keep everything invisible
    this.backdropTarget.classList.remove("hidden")
    this.backdropTarget.classList.add("flex")
    this.choicePanelTarget.classList.remove("hidden")
    this.guestPanelTarget.classList.add("hidden")
    document.body.classList.add("overflow-hidden")

    // Step 2: Force synchronous reflow - ensures flexbox layout is calculated
    // before anything becomes visible (prevents positioning flash)
    void this.backdropTarget.offsetHeight

    // Step 3: Now trigger animations (layout is already computed)
    this.backdropTarget.classList.add("opacity-100")
    this.backdropTarget.classList.remove("opacity-0")
    this.choicePanelTarget.classList.remove("scale-95", "opacity-0")
    this.choicePanelTarget.classList.add("scale-100", "opacity-100")

    this.trapFocus(this.choicePanelTarget)
  }

  closeModal() {
    // Animate out current panel
    if (!this.choicePanelTarget.classList.contains("hidden")) {
      this.choicePanelTarget.classList.add("scale-95", "opacity-0")
      this.choicePanelTarget.classList.remove("scale-100", "opacity-100")
    }
    if (!this.guestPanelTarget.classList.contains("hidden")) {
      this.guestPanelTarget.classList.add("scale-95", "opacity-0")
      this.guestPanelTarget.classList.remove("scale-100", "opacity-100")
    }

    // Animate backdrop out
    this.backdropTarget.classList.add("opacity-0")
    this.backdropTarget.classList.remove("opacity-100")

    // Hide after animation
    setTimeout(() => {
      this.backdropTarget.classList.add("hidden")
      this.backdropTarget.classList.remove("flex")
      document.body.classList.remove("overflow-hidden")
    }, 200)
  }

  showGuestForm() {
    // Animate choice panel out
    this.choicePanelTarget.classList.add("scale-95", "opacity-0")
    this.choicePanelTarget.classList.remove("scale-100", "opacity-100")

    // After animation, swap panels
    setTimeout(() => {
      this.choicePanelTarget.classList.add("hidden")
      this.guestPanelTarget.classList.remove("hidden")

      // Animate guest panel in
      requestAnimationFrame(() => {
        this.guestPanelTarget.classList.remove("scale-95", "opacity-0")
        this.guestPanelTarget.classList.add("scale-100", "opacity-100")
      })

      this.trapFocus(this.guestPanelTarget)
      this.focusFirstInput()
    }, 150)
  }

  backToChoice() {
    // Animate guest panel out
    this.guestPanelTarget.classList.add("scale-95", "opacity-0")
    this.guestPanelTarget.classList.remove("scale-100", "opacity-100")

    // After animation, swap panels
    setTimeout(() => {
      this.guestPanelTarget.classList.add("hidden")
      this.choicePanelTarget.classList.remove("hidden")

      // Animate choice panel in
      requestAnimationFrame(() => {
        this.choicePanelTarget.classList.remove("scale-95", "opacity-0")
        this.choicePanelTarget.classList.add("scale-100", "opacity-100")
      })

      this.trapFocus(this.choicePanelTarget)
    }, 150)
  }

  handleBackdropClick(event) {
    // Only close if clicking directly on the backdrop, not on panel content
    if (event.target === this.backdropTarget) {
      this.closeModal()
    }
  }

  handleEscapeKey(event) {
    if (event.key === "Escape" && !this.backdropTarget.classList.contains("hidden")) {
      this.closeModal()
    }
  }

  trapFocus(panel) {
    const focusableElements = panel.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    firstElement.focus()

    panel.addEventListener("keydown", (event) => {
      if (event.key !== "Tab") return

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        }
      }
    })
  }

  focusFirstInput() {
    const firstInput = this.guestPanelTarget.querySelector("input[type='text'], input[type='email']")
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 100)
    }
  }
}
